import { useEffect, useRef } from "react";

/**
 * Keep a page current without the reviewer reloading the browser.
 *
 * An application changes while HQ is open — an applicant resubmits, cancels, or
 * corrects a document — and until now the only way to see that was F5. A review
 * queue that silently goes stale is worse than a slow one: the reviewer acts on
 * what they can see, and what they can see is wrong.
 *
 * Polling rather than Supabase Realtime, for now, because no table is in the
 * `supabase_realtime` publication — a subscription would deliver nothing until
 * a migration adds one. This works today and can be swapped underneath without
 * any page changing.
 *
 * ⚠️ Callers must refresh SILENTLY — without flipping a loading flag. A poll
 * that swaps the page for a spinner and back reads as the page reloading itself
 * on a timer, which is worse than stale data: it loses scroll position and
 * whatever was being read. See the `silent` option on each page's `load`.
 *
 * Paused when the tab is hidden. A background tab polling every few seconds is
 * pure waste, and the moment it comes back is exactly when a refresh matters,
 * so returning to the tab refetches immediately.
 */
export function useLiveRefresh(
  refresh: () => void,
  { intervalMs = 20_000, enabled = true }: { intervalMs?: number; enabled?: boolean } = {},
) {
  // A ref so a caller can pass an inline arrow without restarting the timer on
  // every render, which would mean it never fires.
  const latest = useRef(refresh);
  useEffect(() => {
    latest.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (!enabled) return;

    const tick = () => {
      if (document.visibilityState === "visible") latest.current();
    };
    const timer = window.setInterval(tick, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === "visible") latest.current();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, intervalMs]);
}
